import type { AppLocale, MessageTree } from "@airange/shared/i18n-common";

export const redblueSchemeDetailReadinessPageMessages: Record<AppLocale, MessageTree> = {
  "zh-CN": {
    redblue: {
      pages: {
        schemeDetail: {
          readiness: {
            selectedReadyTitle: "当前查看版本可发起演练",
            selectedBlockedTitle: "当前查看版本暂不能发起演练",
            selectedReadyDescription: "当前查看版本已满足发起演练条件，可以基于该版本创建演练。",
            selectedBlockedDescription: "发起演练前，还需要补齐以下配置：",
            selectedLoadingTitle: "正在校验当前查看版本发起条件",
            selectedLoadingDescription:
              "系统正在校验当前查看版本的拓扑绑定、阵营映射、任务评分配置、报告模板等前置条件。",
            selectedUnavailableTitle: "无法校验当前查看版本发起条件",
            selectedUnavailableDescription:
              "校验接口读取失败，当前不能确认该版本是否可发起演练；请先排查接口或数据状态。",
            publishedLaunchStatusReady: "发起入口已开放：按当前发布版本创建演练。",
            publishedLaunchStatusBlocked: "发起入口关闭：当前发布版本未满足前置条件。",
            publishedLaunchStatusLoading: "正在校验发起入口状态。",
            publishedLaunchStatusMissing: "发起入口关闭：还没有发布版本。",
            publishedLaunchStatusUnavailable: "发起入口状态暂不可确认，请先排查校验接口。",
            issues: {
              schemeArchived: "方案已归档，不能再发起演练",
              schemeVersionNotPublished: "先发布当前方案版本",
              topologyBindingMissing: "绑定已发布的拓扑版本",
              topologyBindingIncomplete: "补齐拓扑绑定信息",
              topologyRolesMissing: "已绑定拓扑需要提供显式阵营角色",
              topologyRoleUnmapped: "为拓扑角色补齐 RedBlue 阵营类型",
              topologyRolesInvalid: "修正拓扑阵营角色合同",
              schemeCampsMissing: "至少配置一个参演阵营",
              schemeSeatsMissing: "至少配置一个席位",
              taskScoringConfigMissing: "补齐每个任务的评分方式和满分",
              taskScoringConfigInvalid: "修正任务评分配置",
              reportTemplatesMissing: "至少配置一个报告模板",
              campMappingMissing: "补齐拓扑阵营映射",
              campMappingIncomplete: "补齐方案阵营与拓扑阵营的映射关系",
              campTopologyRefMissing: "为参演阵营绑定拓扑阵营引用",
              taskDispatchTargetRefMissing: "补齐任务下发目标引用",
              taskDispatchTargetInvalid: "修正任务下发目标配置",
              unknown: "发现未登记的发起条件校验项：{code}",
            },
          },
        },
      },
    },
  },
  en: {
    redblue: {
      pages: {
        schemeDetail: {
          readiness: {
            selectedReadyTitle: "Current viewing version can launch an activity",
            selectedBlockedTitle: "Current viewing version cannot launch an activity yet",
            selectedReadyDescription:
              "The currently viewed version satisfies the activity launch conditions and can be used to create an activity.",
            selectedBlockedDescription:
              "Complete the following configuration before launching an activity:",
            selectedLoadingTitle: "Checking current viewing version launch conditions",
            selectedLoadingDescription:
              "The system is checking topology binding, camp mapping, task scoring config, report templates, and other prerequisites for the current viewing version.",
            selectedUnavailableTitle: "Current viewing version launch conditions are unavailable",
            selectedUnavailableDescription:
              "The validation query failed, so the system cannot confirm whether this version can launch an activity yet.",
            publishedLaunchStatusReady:
              "Launch entry is open: activities use the current published version.",
            publishedLaunchStatusBlocked:
              "Launch entry is closed: the current published version is missing prerequisites.",
            publishedLaunchStatusLoading: "Checking launch entry status.",
            publishedLaunchStatusMissing: "Launch entry is closed: no published version yet.",
            publishedLaunchStatusUnavailable:
              "Launch entry status is unavailable; check the readiness query first.",
            issues: {
              schemeArchived: "The scheme is archived and cannot launch an activity",
              schemeVersionNotPublished: "Publish the current scheme version first",
              topologyBindingMissing: "Bind a published topology version",
              topologyBindingIncomplete: "Complete the topology binding details",
              topologyRolesMissing: "Provide explicit camp roles in the bound topology",
              topologyRoleUnmapped: "Complete the RedBlue camp type for topology roles",
              topologyRolesInvalid: "Fix the topology camp role contract",
              schemeCampsMissing: "Configure at least one participating camp",
              schemeSeatsMissing: "Configure at least one seat",
              taskScoringConfigMissing: "Complete each task's score kind and max score",
              taskScoringConfigInvalid: "Fix the task scoring config",
              reportTemplatesMissing: "Configure at least one report template",
              campMappingMissing: "Complete the topology camp mapping",
              campMappingIncomplete: "Complete the mapping between scheme camps and topology camps",
              campTopologyRefMissing: "Bind each participating camp to a topology camp reference",
              taskDispatchTargetRefMissing: "Complete the task dispatch target reference",
              taskDispatchTargetInvalid: "Fix the task dispatch target configuration",
              unknown: "Unregistered launch-condition issue: {code}",
            },
          },
        },
      },
    },
  },
  ja: {
    redblue: {
      pages: {
        schemeDetail: {
          readiness: {
            selectedReadyTitle: "現在表示中の版で演習を起動できます",
            selectedBlockedTitle: "現在表示中の版ではまだ演習を起動できません",
            selectedReadyDescription:
              "現在表示中の版は演習起動条件を満たしており、この版から演習を作成できます。",
            selectedBlockedDescription: "演習起動前に、以下の設定を補完してください:",
            selectedLoadingTitle: "現在表示中の版の起動条件を確認しています",
            selectedLoadingDescription:
              "現在表示中の版について、トポロジー紐付け、陣営マッピング、タスク採点設定、レポートテンプレートなどを確認しています。",
            selectedUnavailableTitle: "現在表示中の版の起動条件を確認できません",
            selectedUnavailableDescription:
              "検証クエリに失敗したため、この版で演習を起動できるかまだ確認できません。",
            publishedLaunchStatusReady:
              "起動入口は利用可能です。現在の公開版から演習を作成します。",
            publishedLaunchStatusBlocked:
              "起動入口は閉じています。現在の公開版に前提条件の不足があります。",
            publishedLaunchStatusLoading: "起動入口の状態を確認しています。",
            publishedLaunchStatusMissing: "起動入口は閉じています。公開版がまだありません。",
            publishedLaunchStatusUnavailable:
              "起動入口の状態を確認できません。先に検証クエリを確認してください。",
            issues: {
              schemeArchived: "プランがアーカイブ済みのため、演習を起動できません",
              schemeVersionNotPublished: "先に現在のプラン版を公開してください",
              topologyBindingMissing: "公開済みトポロジー版を紐付けてください",
              topologyBindingIncomplete: "トポロジー紐付け情報を補完してください",
              topologyRolesMissing: "紐付け済みトポロジーに明示的な陣営ロールを提供してください",
              topologyRoleUnmapped: "トポロジーロールに RedBlue 陣営種別を設定してください",
              topologyRolesInvalid: "トポロジー陣営ロールの契約を修正してください",
              schemeCampsMissing: "参演陣営を少なくとも 1 件設定してください",
              schemeSeatsMissing: "席位を少なくとも 1 件設定してください",
              taskScoringConfigMissing: "各タスクの採点方式と満点を補完してください",
              taskScoringConfigInvalid: "タスク採点設定を修正してください",
              reportTemplatesMissing: "レポートテンプレートを少なくとも 1 件設定してください",
              campMappingMissing: "トポロジー陣営マッピングを補完してください",
              campMappingIncomplete: "プラン陣営とトポロジー陣営のマッピングを補完してください",
              campTopologyRefMissing: "参演陣営にトポロジー陣営参照を紐付けてください",
              taskDispatchTargetRefMissing: "タスク配信先参照を補完してください",
              taskDispatchTargetInvalid: "タスク配信先設定を修正してください",
              unknown: "未登録の起動条件検証項目です: {code}",
            },
          },
        },
      },
    },
  },
};
